import React, { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { faCalendarDay, faClock, faLongArrowRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { addMinutes, set } from "date-fns";
import moment from "moment";

import DateInput from "~/calendar/EventForm/DateInput";
import { TimeSelector } from "~/calendar/EventForm/TimeSelector";
import useDateTimeState from "~/calendar/hooks/useDateTimeState";
import { dateToTimeValue, type TimeValue } from "~/calendar/hooks/useTimeOptions";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { Button, toast, useModals } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";

import type { MeetingEvent } from "../common/MeetingSelect";
import NoteAndMeetingLinkerModal from "./NoteAndMeetingLinkerModal";
import { updateNote } from "./NoteCard";

import type { NoteTimeSelectorModal_note$key } from "~/__generated__/NoteTimeSelectorModal_note.graphql";

import styles from "./css/NoteTimeSelectorModal.module.css";

interface NoteTimeSelectorModalProps {
	closeModal: () => void;
	note: NoteTimeSelectorModal_note$key;
	onChangeLinkedMeeting?: (event: MeetingEvent) => void;
}

export default function NoteTimeSelectorModal({
	closeModal,
	note: noteKey,
	onChangeLinkedMeeting,
}: NoteTimeSelectorModalProps) {
	const note = useFragment(
		graphql`
			fragment NoteTimeSelectorModal_note on Note {
				id
				createdAt
				eventStart
				eventEnd
			}
		`,
		noteKey,
	);

	const modals = useModals();
	const { createdAt, eventStart, eventEnd } = note;

	const [timeState, changeTime] = useDateTimeState(() => {
		return {
			isAllDay: false, // unused in this component
			start: eventStart ? new Date(eventStart) : createdAt ? new Date(createdAt) : new Date(),
			end: addMinutes(
				eventEnd
					? new Date(eventEnd)
					: eventStart
						? new Date(eventStart)
						: createdAt
							? new Date(createdAt)
							: new Date(),
				30,
			),
		};
	});

	const onChangeDateTime = useCallback(
		(startDateTime: string, endDateTime: string) => {
			const noteUpdates = {
				/* eslint-disable camelcase */
				event_guid: null,
				event_start: moment(startDateTime).format(),
				event_end: moment(endDateTime).format(),
				/* eslint-enable camelcase */
			};

			// eslint-disable-next-line camelcase
			dispatchAnalyticsTrack("[meetings] Change note date and time", { note_id: note.id });

			updateNote(note.id, noteUpdates, ({ updateNote }) => {
				if (updateNote && updateNote.ok) {
					const oldDateTime = moment(note.eventStart || "");
					const scroll = oldDateTime.isValid() && oldDateTime.isBefore(startDateTime) ? "up" : "down";
					// TODO: don't show this in the meeting modal
					toast.success(`New date and time saved. You may need to scroll ${scroll} to find the note.`);
				} else {
					toast.error("The new date and time did not save properly. Please refresh this page and try again.");
				}
			});
		},
		[note.eventStart, note.id],
	);

	const changeStartTime = useCallback(
		(time: TimeValue) => {
			const newValue = set(timeState.start, { hours: time.hour, minutes: time.minute });
			changeTime({
				type: "SHIFT",
				newTime: newValue,
			});
		},
		[timeState.start, changeTime],
	);

	const changeStartDate = useCallback(
		(date: Date) => {
			const newValue = set(date, { hours: timeState.start.getHours(), minutes: timeState.start.getMinutes() });
			changeTime({
				type: "SHIFT",
				newTime: newValue,
			});
		},
		[timeState, changeTime],
	);

	const changeEndTime = useCallback(
		(time: TimeValue) => {
			const newValue = set(timeState.end, { hours: time.hour, minutes: time.minute });
			changeTime({
				type: "CHANGE_END",
				newTime: newValue,
			});
		},
		[timeState.end, changeTime],
	);

	const openNoteAndMeetingLinkerModal = useCallback(() => {
		if (onChangeLinkedMeeting) {
			modals.open(({ close }) => (
				<NoteAndMeetingLinkerModal
					closeModal={close}
					currentEvent={undefined} // If this modal is open there shouldn't be an associated meeting
					note={noteKey}
					onChangeLinkedMeeting={onChangeLinkedMeeting}
					onChangeDateTime={onChangeDateTime}
				/>
			));
			closeModal();
		}
	}, [onChangeLinkedMeeting, modals, closeModal, noteKey, onChangeDateTime]);

	const closeModalAndChangeDateTime = useCallback(() => {
		onChangeDateTime(timeState.start.toISOString(), timeState.end.toISOString());
		closeModal();
	}, [onChangeDateTime, timeState, closeModal]);

	return (
		<Modal onClose={closeModal} width="20rem">
			<Modal.Header onClose={closeModal} noBottomBorder>
				<Modal.Title>Change note date & time</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.modalBody}>
				<div className={styles.formSectionRow}>
					<FontAwesomeIcon icon={faCalendarDay} className={styles.formSectionRowIcon} />
					<DateInput date={timeState.start} setDate={changeStartDate} labelFormat="long" />
				</div>
				<div className={styles.formSectionRow}>
					<FontAwesomeIcon icon={faClock} className={styles.formSectionRowIcon} />
					<span className={styles.timeSelectorWidthManager}>
						<TimeSelector time={dateToTimeValue(timeState.start)} setTime={changeStartTime} />
					</span>
					<FontAwesomeIcon icon={faLongArrowRight} className={styles.formSectionRowIcon} />
					<TimeSelector
						time={dateToTimeValue(timeState.end)}
						setTime={changeEndTime}
						timeToCompare={dateToTimeValue(timeState.start)}
					/>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Modal.FooterSecondaryActions>
					<Button variant="secondary" onClick={openNoteAndMeetingLinkerModal}>
						Link to a meeting
					</Button>
				</Modal.FooterSecondaryActions>
				<Button variant="primary" onClick={closeModalAndChangeDateTime}>
					Apply changes
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
